import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  IPolicy,
  INewPolicyPayload,
  IPolicyConditionPayload,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useGetPolicySettingsQuery,
} from '@entities/policy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Stack, Typography, IconButton } from '@mui/material';
import { FormItemWithError, ISelectMultiOption, SelectMulti } from '@shared/components/Form';
import { useModal } from '@shared/components/Modal';

type ConditionState = {
  currency: ISelectMultiOption[];
  method: ISelectMultiOption[];
  traffic: ISelectMultiOption[];
  merchants: ISelectMultiOption[];
};

interface IProps {
  policy?: IPolicy;
}

const PolicyForm: FC<IProps> = ({ policy }) => {
  const { closeModal } = useModal();
  const { data: policySettings } = useGetPolicySettingsQuery();

  const [name, setName] = useState(policy?.name ?? '');
  const [description, setDescription] = useState(policy?.description ?? '');
  // const [active, setActive] = useState<boolean>(policy?.active ?? false);
  const shouldScrollToNewConditionRef = useRef(false);
  const lastConditionRef = useRef<HTMLDivElement | null>(null);
  const [createPolicy, { isLoading: isCreating, isError, error: createPolicyError }] =
    useCreatePolicyMutation();
  const [updatePolicy, { isLoading: isUpdating }] = useUpdatePolicyMutation();

  const [conditions, setConditions] = useState<ConditionState[]>(
    policy?.conditions?.length
      ? policy.conditions.map(item => ({
          currency: Array.isArray(item.currency)
            ? item.currency.map(value => ({ value, label: value }))
            : item.currency
              ? [{ value: item.currency, label: item.currency }]
              : [],
          method: Array.isArray(item.method)
            ? item.method.map(value => ({ value, label: value }))
            : item.method
              ? [{ value: item.method, label: item.method }]
              : [],
          traffic: Array.isArray(item.traffic)
            ? item.traffic.map(value => ({ value, label: value }))
            : item.traffic
              ? [{ value: item.traffic, label: item.traffic }]
              : [],
          merchants: item.merchants?.map(m => ({ value: m, label: m })) ?? [],
        }))
      : [{ currency: [], method: [], traffic: [], merchants: [] }],
  );

  const normalizedConditions: IPolicyConditionPayload[] = useMemo(
    () =>
      conditions.map(c => ({
        currency: c.currency.map(opt => opt.value),
        method: c.method.map(opt => opt.value),
        traffic: c.traffic.map(opt => opt.value),
        merchants: c.merchants.map(opt => opt.value),
      })),
    [conditions],
  );

  const buildOptions = (source?: Record<string, string>) =>
    Object.entries(source ?? {}).map(([value, label]) => ({
      value,
      label,
    }));

  const currencyOptions = useMemo<ISelectMultiOption[]>(
    () =>
      buildOptions(policySettings?.data?.currency).length
        ? buildOptions(policySettings?.data?.currency)
        : (
            policy?.conditions?.flatMap(c =>
              Array.isArray(c.currency) ? c.currency : c.currency ? [c.currency] : [],
            ) || []
          ).map(code => ({
            value: code,
            label: code,
          })),
    [policySettings?.data?.currency, policy?.conditions],
  );

  const methodOptions = useMemo<ISelectMultiOption[]>(
    () =>
      buildOptions(policySettings?.data?.method).length
        ? buildOptions(policySettings?.data?.method)
        : (
            policy?.conditions?.flatMap(c =>
              Array.isArray(c.method) ? c.method : c.method ? [c.method] : [],
            ) || []
          ).map(code => ({
            value: code,
            label: code,
          })),
    [policySettings?.data?.method, policy?.conditions],
  );

  const merchantOptions = useMemo<ISelectMultiOption[]>(
    () =>
      buildOptions(policySettings?.data?.merchants).length
        ? buildOptions(policySettings?.data?.merchants)
        : (policy?.conditions?.flatMap(t => t.merchants ?? []) || []).map(code => ({
            value: code,
            label: code,
          })),
    [policySettings?.data?.merchants, policy?.conditions],
  );

  const trafficOptions: ISelectMultiOption[] = useMemo(
    () =>
      buildOptions(policySettings?.data?.traffic).length
        ? buildOptions(policySettings?.data?.traffic)
        : (
            policy?.conditions?.flatMap(c =>
              Array.isArray(c.traffic) ? c.traffic : c.traffic ? [c.traffic] : [],
            ) || []
          ).map(code => ({
            value: code,
            label: code,
          })),
    [policySettings?.data?.traffic, policy?.conditions],
  );

  const updateCondition = (
    index: number,
    key: keyof ConditionState,
    value: ISelectMultiOption[],
  ) => {
    setConditions(prev => prev.map((cond, i) => (i === index ? { ...cond, [key]: value } : cond)));
  };

  const addCondition = () => {
    shouldScrollToNewConditionRef.current = true;
    setConditions(prev => [...prev, { currency: [], method: [], traffic: [], merchants: [] }]);
  };

  const removeCondition = (index: number) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (!shouldScrollToNewConditionRef.current) return;
    if (lastConditionRef.current) {
      lastConditionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    shouldScrollToNewConditionRef.current = false;
  }, [conditions.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: INewPolicyPayload = {
      name,
      description,
      // don't send for now
      // active: policy ? active : false,
      conditions: normalizedConditions,
    };
    try {
      if (policy) {
        await updatePolicy({ id: policy.id, ...payload }).unwrap();
      } else {
        await createPolicy(payload).unwrap();
      }
      closeModal();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form className='modal-sticky-layout' onSubmit={handleSubmit}>
      <div className='modal-sticky-header'>
        <Typography variant='h6'>{policy ? 'Edit policy' : 'New policy'}</Typography>
      </div>
      <div className='modal-sticky-content'>
        <Stack spacing={2}>
          <FormItemWithError name='name' label='Name' isError={isError} error={createPolicyError}>
            <input
              id='name'
              placeholder='Name'
              type='text'
              name='name'
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </FormItemWithError>

          <FormItemWithError
            name='description'
            label='Description'
            isError={isError}
            error={createPolicyError}
          >
            <textarea
              id='description'
              name='description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Description'
            />
          </FormItemWithError>
          {/* {policy && (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Switch checked={active} onChange={(_, checked) => setActive(checked)} />
              <Typography variant='body2'>{active ? 'Active' : 'Inactive'}</Typography>
            </Stack>
          )} */}
          <Typography variant='caption' color='text.secondary'>
            Rules configuration will be added in the next step.
          </Typography>

          <Stack spacing={2}>
            <Typography variant='subtitle1' fontWeight={700}>
              Traffic & Merchants Conditions
            </Typography>

            {conditions.map((condition, index) => (
              <div key={index} ref={index === conditions.length - 1 ? lastConditionRef : null}>
                <Stack spacing={2} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='subtitle2'>Condition #{index + 1}</Typography>
                    {conditions.length > 1 && (
                      <IconButton size='small' color='error' onClick={() => removeCondition(index)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant='caption' color='text.secondary'>
                      Currencies
                    </Typography>
                    <SelectMulti
                      name={`conditions[${index}][currency][]`}
                      options={currencyOptions}
                      value={condition.currency}
                      onChange={val =>
                        updateCondition(index, 'currency', val as ISelectMultiOption[])
                      }
                    />
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant='caption' color='text.secondary'>
                      Methods
                    </Typography>
                    <SelectMulti
                      name={`conditions[${index}][method][]`}
                      options={methodOptions}
                      value={condition.method}
                      onChange={val =>
                        updateCondition(index, 'method', val as ISelectMultiOption[])
                      }
                    />
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant='caption' color='text.secondary'>
                      Traffic
                    </Typography>
                    <SelectMulti
                      name={`conditions[${index}][traffic][]`}
                      options={trafficOptions}
                      value={condition.traffic}
                      onChange={val =>
                        updateCondition(index, 'traffic', val as ISelectMultiOption[])
                      }
                    />
                  </Stack>

                  <Stack spacing={1}>
                    <Typography variant='caption' color='text.secondary'>
                      Merchants
                    </Typography>
                    <SelectMulti
                      name={`conditions[${index}][merchants][]`}
                      options={merchantOptions}
                      value={condition.merchants}
                      onChange={val =>
                        updateCondition(index, 'merchants', val as ISelectMultiOption[])
                      }
                    />
                  </Stack>
                </Stack>
              </div>
            ))}

            <button type='button' className='secondary-btn' onClick={addCondition}>
              <AddIcon fontSize='small' /> Add Traffic Condition
            </button>
          </Stack>
        </Stack>
      </div>
      <div className='modal-sticky-footer'>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <button type='button' className='secondary-btn' onClick={closeModal}>
            Cancel
          </button>
          <button type='submit' className='primary-btn' disabled={isCreating || isUpdating}>
            {isCreating || isUpdating ? 'Saving...' : policy ? 'Save changes' : 'Create policy'}
          </button>
        </Stack>
      </div>
    </form>
  );
};

export default PolicyForm;
